import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { ArrowLeft, Loader2, ShieldCheck, Eye, Pencil, Ban } from "lucide-react";

const LEVEL_META = {
  none: { label: "No access", icon: Ban, cls: "text-[#9FA1A4]" },
  view: { label: "View only", icon: Eye, cls: "text-[#b7791f]" },
  edit: { label: "View & Edit", icon: Pencil, cls: "text-[#1f9d55]" },
};

export default function ConsolePermissions({ onBack }) {
  const [meta, setMeta] = useState(null);
  const [perms, setPerms] = useState(null);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  const load = async () => {
    const { data } = await api.get("/console/permissions");
    setMeta(data); setPerms(JSON.parse(JSON.stringify(data.permissions))); setDirty(false);
  };
  useEffect(() => { load().catch((e) => toast.error(formatApiError(e.response?.data?.detail))); }, []);

  const setCell = (role, module, level) => {
    setPerms((p) => ({ ...p, [role]: { ...p[role], [module]: level } }));
    setDirty(true);
  };

  const save = async () => {
    setSaving(true);
    try { const { data } = await api.put("/console/permissions", { permissions: perms }); setPerms(data.permissions); setDirty(false); toast.success("Permission groups saved. Applies on each user's next page load."); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setSaving(false); }
  };

  if (!meta || !perms) return <main className="px-6 md:px-10 py-24 max-w-7xl mx-auto text-center" data-testid="console-permissions-loading"><Loader2 className="h-6 w-6 animate-spin text-[#c4c5cc] mx-auto" /></main>;

  return (
    <main className="px-6 md:px-10 py-10 max-w-6xl mx-auto" data-testid="console-permissions-page">
      <button data-testid="permissions-back" onClick={onBack} className="flex items-center gap-1.5 text-sm text-[#595D62] hover:text-[#06060C] mb-6"><ArrowLeft className="h-4 w-4" /> Back to team</button>
      <div className="flex items-start gap-3 mb-2">
        <div className="h-11 w-11 rounded-2xl bg-[#EDE7FF] text-[#723FFF] flex items-center justify-center shrink-0"><ShieldCheck className="h-5 w-5" /></div>
        <div>
          <h1 className="font-display text-3xl md:text-4xl font-semibold tracking-tight text-[#06060C]">Permission groups</h1>
          <p className="mt-2 text-sm text-[#9FA1A4] max-w-2xl">Choose what each agency role can see and do across the Mission Console. Super Admins always have full access and can't be restricted.</p>
        </div>
      </div>

      <div className="rounded-3xl border border-[#EEEFEF] premium-shadow bg-white overflow-hidden mt-6">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="permissions-table">
            <thead>
              <tr className="border-b border-[#f0f0f3] text-left font-mono-data text-[10px] uppercase tracking-wider text-[#9FA1A4]">
                <th className="px-4 py-3 sticky left-0 bg-white">Module</th>
                {meta.roles.map((r) => (
                  <th key={r} className="px-4 py-3">{meta.role_labels[r]}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {meta.modules.map((m) => (
                <tr key={m} data-testid={`perm-row-${m}`} className="border-b border-[#f6f6f9] hover:bg-[#FCFCFE]">
                  <td className="px-4 py-3 font-medium text-[#06060C] sticky left-0 bg-white">{meta.module_labels[m]}</td>
                  {meta.roles.map((r) => {
                    const level = perms[r]?.[m] || "none";
                    const Meta = LEVEL_META[level];
                    return (
                      <td key={r} className="px-4 py-2.5">
                        <Select value={level} onValueChange={(v) => setCell(r, m, v)}>
                          <SelectTrigger data-testid={`perm-select-${r}-${m}`} className="h-9 w-40 bg-[#F6F6FD] border-[#EEEFEF]">
                            <span className={`inline-flex items-center gap-1.5 ${Meta.cls}`}><Meta.icon className="h-3.5 w-3.5" /> {Meta.label}</span>
                          </SelectTrigger>
                          <SelectContent className="bg-white border-[#EEEFEF]">
                            {meta.levels.map((lv) => {
                              const LM = LEVEL_META[lv];
                              return <SelectItem key={lv} value={lv}><span className={`inline-flex items-center gap-1.5 ${LM.cls}`}><LM.icon className="h-3.5 w-3.5" /> {LM.label}</span></SelectItem>;
                            })}
                          </SelectContent>
                        </Select>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="flex items-center justify-between gap-3 mt-6">
        <p className="text-xs text-[#9FA1A4]">Changes take effect the next time each user loads the console.</p>
        <div className="flex items-center gap-2">
          {dirty && <Button data-testid="permissions-reset" variant="outline" onClick={() => load()} className="border-[#EEEFEF] bg-white text-[#595D62] rounded-full">Discard</Button>}
          <Button data-testid="permissions-save" onClick={save} disabled={saving || !dirty} className="bg-[#06060C] text-white hover:bg-[#1c1c24] rounded-full">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save changes"}</Button>
        </div>
      </div>
    </main>
  );
}
